import { useEffect, useState, useCallback, useRef } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  apiGetPayrollRuns,
  apiCreatePayrollRun,
  apiGetPayrollRun,
  apiUpdatePayrollEntry,
  apiUpdatePayrollRunStatus,
  apiDeletePayrollRun,
  apiCreateAdjustment,
  apiDeleteAdjustment,
  apiBulkAdjustSalaries,
  apiBulkAdjustPreview,
  apiGetDepartments,
  type BulkAdjustPreview,
  type PayrollRunSummary,
  type PayrollRunDetail,
  type PayrollEntry,
  type BulkAdjustResult,
} from "@/lib/api";
import { formatMKD } from "@/lib/mk-salary";
import {
  DollarSign,
  TrendingUp,
  FileText,
  Banknote,
  Shield,
  Sparkles,
  Sliders,
  TrendingDown,
  Percent,
  CheckCircle,
  ChevronRight,
  Settings2,
} from "lucide-react";
import { MONTH_NAMES_EN, statusConfig } from "@/components/payroll/payroll-constants";
import { PayrollRunDetailView } from "@/components/payroll/PayrollRunDetailView";
import { QuickCalculator } from "@/components/payroll/QuickCalculatorCard";
import { TaxSettingsTab } from "@/components/payroll/TaxSettingsTab";

const Payroll = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isCLevel = user?.role?.subRole === "c_level";
  const { toast } = useToast();
  const [runs, setRuns] = useState<PayrollRunSummary[]>([]);
  const [selectedRun, setSelectedRun] = useState<PayrollRunDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMonth, setNewMonth] = useState(new Date().getMonth() + 1);
  const [newYear, setNewYear] = useState(new Date().getFullYear());
  const [isCreatingRun, setIsCreatingRun] = useState(false);

  // Bulk salary adjustment
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkType, setBulkType] = useState<"percentage" | "fixed">("percentage");
  const [bulkValue, setBulkValue] = useState<string>("");
  const [bulkFilter, setBulkFilter] = useState<"all" | "department">("all");
  const [bulkDepartments, setBulkDepartments] = useState<string[]>([]);
  const [bulkDeptOptions, setBulkDeptOptions] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<BulkAdjustResult | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<BulkAdjustPreview | null>(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const translatedMonthNames = t("payroll.monthNames", { returnObjects: true });
  const monthNames = Array.isArray(translatedMonthNames) ? translatedMonthNames : MONTH_NAMES_EN;
  const getMonthName = (month: number) => monthNames[month - 1] ?? MONTH_NAMES_EN[month - 1];

  const loadRuns = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiGetPayrollRuns();
      setRuns(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const loadRunDetail = useCallback(async (runId: number) => {
    try {
      setDetailLoading(true);
      const data = await apiGetPayrollRun(runId);
      setSelectedRun(data);
    } catch (err: any) {
      toast({
        title: t("common.error"),
        description: err?.message ?? t("payroll.loadRunError"),
        variant: "destructive",
      });
    } finally {
      setDetailLoading(false);
    }
  }, [toast, t]);

  const handleCreateRun = async () => {
    setIsCreatingRun(true);
    try {
      const run = await apiCreatePayrollRun({ month: newMonth, year: newYear });
      setShowCreateModal(false);
      await loadRuns();
      await loadRunDetail(run.id);
      toast({
        title: t("payroll.runCreated"),
        description: String(t("payroll.runCreatedDesc", {
          month: getMonthName(newMonth),
          year: newYear.toString(),
          count: "all",
        } as any)),
      });
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    } finally {
      setIsCreatingRun(false);
    }
  };

  const handleUpdateEntry = async (entry: PayrollEntry, amount: number, inputType: "gross" | "net") => {
    if (!selectedRun) return;
    try {
      await apiUpdatePayrollEntry(selectedRun.id, entry.id, { amount, inputType });
      await loadRunDetail(selectedRun.id);
      toast({ title: t("payroll.entryUpdated"), description: t("payroll.entryUpdatedDesc", { name: entry.employeeName || "" }) });
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    }
  };

  const handleAddAdjustment = async (entryId: number, data: { type: string; name: string; amount: number; applyOn: string; notes?: string }) => {
    if (!selectedRun) return;
    try {
      await apiCreateAdjustment(selectedRun.id, entryId, data);
      await loadRunDetail(selectedRun.id);
      toast({ title: t("payroll.adjustmentAdded") });
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    }
  };

  const handleDeleteAdjustment = async (entryId: number, adjId: number) => {
    if (!selectedRun) return;
    try {
      await apiDeleteAdjustment(selectedRun.id, entryId, adjId);
      await loadRunDetail(selectedRun.id);
      toast({ title: t("payroll.adjustmentRemoved") });
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    }
  };

  const handleStatusChange = async (status: string, sendEmail: boolean) => {
    if (!selectedRun) return;
    try {
      await apiUpdatePayrollRunStatus(selectedRun.id, status, { sendEmailNotification: sendEmail });
      await loadRunDetail(selectedRun.id);
      await loadRuns();
      toast({ title: t("payroll.statusUpdated") });
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    }
  };

  const handleDeleteRun = async () => {
    if (!selectedRun || !confirm(t("payroll.deleteConfirm"))) return;
    try {
      await apiDeletePayrollRun(selectedRun.id);
      setSelectedRun(null);
      await loadRuns();
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    }
  };

  const fetchBulkPreview = useCallback((type: "percentage" | "fixed", val: string, filter: "all" | "department", depts: string[]) => {
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    const numVal = parseFloat(val);
    if (!val || isNaN(numVal) || numVal === 0 || (filter === "department" && depts.length === 0)) {
      setBulkPreview(null);
      return;
    }
    previewDebounceRef.current = setTimeout(async () => {
      setBulkPreviewLoading(true);
      try {
        const p = await apiBulkAdjustPreview({ adjustmentType: type, value: numVal, filterType: filter, departments: filter === "department" ? depts : undefined });
        setBulkPreview(p);
      } catch { setBulkPreview(null); }
      finally { setBulkPreviewLoading(false); }
    }, 400);
  }, []);

  if (selectedRun) {
    return (
      <AppLayout>
        <PayrollRunDetailView
          run={selectedRun}
          loading={detailLoading}
          onBack={() => { setSelectedRun(null); loadRuns(); }}
          onUpdateEntry={handleUpdateEntry}
          onStatusChange={handleStatusChange}
          onDelete={handleDeleteRun}
          onAddAdjustment={handleAddAdjustment}
          onDeleteAdjustment={handleDeleteAdjustment}
          readOnly={isCLevel}
          t={t}
        />
      </AppLayout>
    );
  }

  const handleBulkAdjust = async () => {
    const numVal = parseFloat(bulkValue);
    if (isNaN(numVal) || numVal === 0) {
      toast({ title: t("payroll.bulkInvalidValue"), variant: "destructive" });
      return;
    }
    setBulkLoading(true);
    try {
      const result = await apiBulkAdjustSalaries({
        adjustmentType: bulkType,
        value: numVal,
        filterType: bulkFilter,
        departments: bulkFilter === "department" ? bulkDepartments : undefined,
      });
      setBulkResult(result);
      toast({ title: t("payroll.bulkDone"), description: t("payroll.bulkDoneDesc", { updated: result.updated, skipped: result.skipped }) });
    } catch (err: any) {
      toast({ title: t("common.error"), description: err.message, variant: "destructive" });
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div className="flex items-center justify-between animate-in fade-in slide-in-from-left-5 duration-500">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
              <DollarSign className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{t("payroll.title")}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t("payroll.subtitle")}</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="runs">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger value="runs" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
              <DollarSign className="h-3.5 w-3.5" />{t("payroll.payrollRuns")}
            </TabsTrigger>
            {!isCLevel && (
              <TabsTrigger value="tax" className="flex items-center gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent">
                <Settings2 className="h-3.5 w-3.5" />{t("payroll.taxSettings.tab")}
              </TabsTrigger>
            )}
          </TabsList>

          {!isCLevel && (
            <TabsContent value="tax" className="mt-6">
              <TaxSettingsTab />
            </TabsContent>
          )}

          <TabsContent value="runs" className="mt-6">
            <div className="space-y-6">
              {!isCLevel && (
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={async () => {
                    setBulkResult(null); setBulkValue(""); setBulkFilter("all"); setBulkDepartments([]);
                    try { const depts = await apiGetDepartments(); setBulkDeptOptions(depts.map((d) => d.name)); } catch { /* ignore */ }
                    setBulkDialog(true);
                  }} className="gap-2">
                    <Sliders className="h-4 w-4" />
                    {t("payroll.bulkAdjust")}
                  </Button>
                  <Button onClick={() => setShowCreateModal(!showCreateModal)} className="gap-2">
                    <DollarSign className="h-4 w-4" />
                    {t("payroll.newRun")}
                  </Button>
                </div>
              )}

              {showCreateModal && (
                <Card className="shadow-card animate-in fade-in slide-in-from-top-4 duration-300 border-primary/20">
                  <CardContent className="p-5">
                    <h3 className="font-semibold mb-4">{t("payroll.createRunFor")}</h3>
                    <div className="flex items-center gap-4">
                      <select value={newMonth} onChange={(e) => setNewMonth(parseInt(e.target.value))} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
                        {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                      </select>
                      <input type="number" value={newYear} onChange={(e) => setNewYear(parseInt(e.target.value))} className="h-9 w-24 rounded-md border border-input bg-background px-3 text-sm" />
                      <Button onClick={handleCreateRun} disabled={isCreatingRun}>
                        {isCreatingRun && <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />}
                        {t("payroll.create")}
                      </Button>
                      <Button variant="outline" onClick={() => setShowCreateModal(false)}>{t("common.cancel")}</Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {runs.length > 0 && (
                <div className="rounded-2xl border border-border/60 bg-card shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 delay-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 sm:divide-x divide-border/60 divide-y sm:divide-y-0 lg:divide-y-0">
                    <div className="flex items-center gap-3.5 px-5 py-4">
                      <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-blue-600" /></div>
                      <div className="min-w-0">
                        <p className="text-2xl font-bold text-foreground leading-none tabular-nums">{runs.length}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t("payroll.runs")}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3.5 px-5 py-4">
                      <div className="h-9 w-9 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0"><TrendingUp className="h-4 w-4 text-emerald-600" /></div>
                      <div className="min-w-0">
                        <p className="text-2xl font-bold text-foreground leading-none tabular-nums">{formatMKD(runs.reduce((s, r) => s + r.totalGross, 0))}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t("payroll.totalGross")} · MKD</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3.5 px-5 py-4">
                      <div className="h-9 w-9 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0"><Banknote className="h-4 w-4 text-violet-600" /></div>
                      <div className="min-w-0">
                        <p className="text-2xl font-bold text-foreground leading-none tabular-nums">{formatMKD(runs.reduce((s, r) => s + r.totalNet, 0))}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t("payroll.totalNet")} · MKD</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3.5 px-5 py-4">
                      <div className="h-9 w-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0"><Shield className="h-4 w-4 text-amber-600" /></div>
                      <div className="min-w-0">
                        <p className="text-2xl font-bold text-foreground leading-none tabular-nums">{formatMKD(runs.reduce((s, r) => s + r.totalContributions, 0))}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t("payroll.totalContributions")} · MKD</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />)}
                </div>
              ) : runs.length === 0 ? (
                <Card className="shadow-card">
                  <CardContent className="flex flex-col items-center justify-center py-16 space-y-4">
                    <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center animate-pulse">
                      <Sparkles className="h-10 w-10 text-primary" />
                    </div>
                    <p className="font-medium text-foreground">{t("payroll.noRuns")}</p>
                    <p className="text-sm text-muted-foreground max-w-md">{t("payroll.noRunsDesc")}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {runs.map((run, idx) => {
                    const sc = statusConfig[run.status] || statusConfig.Draft;
                    const StatusIcon = sc.icon;
                    return (
                      <Card key={run.id} className="shadow-card hover:shadow-lg transition-all duration-300 cursor-pointer animate-in fade-in slide-in-from-bottom-4 duration-500 group" style={{ animationDelay: `${idx * 60}ms` }} onClick={() => loadRunDetail(run.id)}>
                        <CardContent className="p-5 flex items-center gap-4">
                          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md shrink-0">
                            <DollarSign className="h-6 w-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-foreground">{getMonthName(run.month)} {run.year}</h3>
                              <Badge variant="outline" className={`text-[10px] border ${sc.color}`}>
                                <StatusIcon className="h-3 w-3 mr-1" />{t(`payroll.${run.status.toLowerCase()}`, run.status)}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{run.entryCount} {t("payroll.employees")} • {t("payroll.gross")}: {formatMKD(run.totalGross)} MKD</p>
                          </div>
                          <div className="hidden sm:flex flex-col items-end gap-1">
                            <p className="text-sm font-semibold text-foreground">{formatMKD(run.totalNet)} MKD</p>
                            <p className="text-xs text-muted-foreground">{t("payroll.totalNet")}</p>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}

              <QuickCalculator t={t} />
            </div>

            {/* Bulk Salary Adjustment Dialog */}
            <Dialog open={bulkDialog} onOpenChange={(open) => { setBulkDialog(open); if (!open) { setBulkResult(null); setBulkPreview(null); } }}>
              <DialogContent className="w-[90vw] max-w-4xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sliders className="h-5 w-5" />
                    {t("payroll.bulkAdjust")}
                  </DialogTitle>
                  <DialogDescription>{t("payroll.bulkAdjustDesc")}</DialogDescription>
                </DialogHeader>

                {!bulkResult ? (
                  <div className="flex gap-6">
                    {/* Left: controls */}
                    <div className="flex flex-col gap-4 w-72 shrink-0">
                      <div>
                        <Label className="mb-2 block">{t("payroll.bulkAdjustType")}</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {(["percentage", "fixed"] as const).map((t2) => (
                            <button key={t2} onClick={() => { setBulkType(t2); setBulkValue(""); setBulkPreview(null); }} className={`flex items-center gap-2 p-3 rounded-lg border text-sm font-medium transition-all ${bulkType === t2 ? "border-primary bg-primary/5 text-primary" : "hover:bg-secondary/50"}`}>
                              {t2 === "percentage" ? <Percent className="h-4 w-4" /> : <DollarSign className="h-4 w-4" />}
                              {t(`payroll.bulkType_${t2}`)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <FloatField
                          label={`${t("payroll.bulkValue")} ${bulkType === "percentage" ? "(%)" : "(MKD)"}`}
                          type="text"
                          placeholder={bulkType === "percentage" ? t("payroll.bulkPercentPlaceholder") : t("payroll.bulkFixedPlaceholder")}
                          value={bulkValue}
                          onChange={(v) => { setBulkValue(v); fetchBulkPreview(bulkType, v, bulkFilter, bulkDepartments); }}
                        />
                        <p className="text-xs text-muted-foreground mt-1 px-1">{t("payroll.bulkValueHint")}</p>
                      </div>

                      <FloatWrapper label={t("payroll.bulkScope")}>
                        <Select value={bulkFilter} onValueChange={(v) => {
                          const f = v as "all" | "department";
                          setBulkFilter(f); setBulkDepartments([]); setBulkPreview(null);
                          if (f === "all") fetchBulkPreview(bulkType, bulkValue, "all", []);
                        }}>
                          <SelectTrigger className="w-full rounded-xl border border-[#d0d5e8] h-14 px-4"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">{t("payroll.bulkScopeAll")}</SelectItem>
                            <SelectItem value="department">{t("payroll.bulkScopeDept")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </FloatWrapper>

                      {bulkFilter === "department" && (
                        <div className="flex-1">
                          <Label className="mb-2 block">{t("payroll.bulkDepartment")}</Label>
                          <div className="border rounded-lg divide-y overflow-y-auto max-h-48">
                            {bulkDeptOptions.length === 0 ? (
                              <p className="text-sm text-muted-foreground px-3 py-2">{t("payroll.bulkNoDepts")}</p>
                            ) : bulkDeptOptions.map((dept) => {
                              const checked = bulkDepartments.includes(dept);
                              return (
                                <label key={dept} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-secondary/50 transition-colors">
                                  <Checkbox checked={checked} onCheckedChange={(v) => {
                                    const next = v ? [...bulkDepartments, dept] : bulkDepartments.filter((d) => d !== dept);
                                    setBulkDepartments(next);
                                    fetchBulkPreview(bulkType, bulkValue, "department", next);
                                  }} />
                                  <span className="text-sm">{dept}</span>
                                </label>
                              );
                            })}
                          </div>
                          {bulkDepartments.length > 0 && <p className="text-xs text-muted-foreground mt-1">{bulkDepartments.length} {t("payroll.bulkDeptsSelected")}</p>}
                        </div>
                      )}

                      <div className="flex gap-2 mt-auto pt-2">
                        <Button variant="outline" onClick={() => setBulkDialog(false)} className="flex-1">{t("common.cancel")}</Button>
                        <Button onClick={handleBulkAdjust} disabled={bulkLoading || !bulkValue || parseFloat(bulkValue) === 0 || (bulkFilter === "department" && bulkDepartments.length === 0)} className="flex-1 gap-2">
                          {bulkLoading ? <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Sliders className="h-4 w-4" />}
                          {t("payroll.bulkApply")}
                        </Button>
                      </div>
                    </div>

                    {/* Right: impact panel */}
                    <div className="flex-1 min-w-0">
                      {!bulkPreview && !bulkPreviewLoading ? (
                        <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground gap-3 rounded-xl border border-dashed p-8">
                          <Sliders className="h-10 w-10 opacity-20" />
                          <p className="text-sm">{t("payroll.bulkImpactHint")}</p>
                        </div>
                      ) : (
                        <div className={`rounded-xl border overflow-hidden h-full flex flex-col ${bulkPreview && bulkPreview.delta > 0 ? "border-green-200" : "border-red-200"}`}>
                          <div className={`px-4 py-3 flex items-center gap-2 text-sm font-semibold shrink-0 ${bulkPreview && bulkPreview.delta > 0 ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                            {bulkPreviewLoading ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : bulkPreview && bulkPreview.delta > 0 ? (
                              <TrendingUp className="h-4 w-4 shrink-0" />
                            ) : (
                              <TrendingDown className="h-4 w-4 shrink-0" />
                            )}
                            {bulkPreviewLoading ? t("payroll.bulkCalculating") : (
                              bulkPreview && bulkPreview.delta > 0 ? t("payroll.bulkImpactIncrease") : t("payroll.bulkImpactDecrease")
                            )}
                          </div>

                          {bulkPreview && !bulkPreviewLoading && (
                            <div className="bg-card p-4 space-y-4 flex-1">
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="p-2.5 rounded-lg bg-secondary/60">
                                  <p className="text-xs text-muted-foreground">{t("payroll.bulkImpactEmployees")}</p>
                                  <p className="text-2xl font-bold">{bulkPreview.affectedCount}</p>
                                </div>
                                <div className={`p-2.5 rounded-lg ${bulkPreview.delta > 0 ? "bg-green-50" : "bg-red-50"}`}>
                                  <p className="text-xs text-muted-foreground">{t("payroll.bulkImpactMonthly")}</p>
                                  <p className={`text-xl font-bold ${bulkPreview.delta > 0 ? "text-green-700" : "text-red-700"}`}>
                                    {bulkPreview.delta > 0 ? "+" : ""}{formatMKD(bulkPreview.delta)}
                                  </p>
                                </div>
                                <div className={`p-2.5 rounded-lg ${bulkPreview.annualDelta > 0 ? "bg-green-50" : "bg-red-50"}`}>
                                  <p className="text-xs text-muted-foreground">{t("payroll.bulkImpactAnnual")}</p>
                                  <p className={`text-xl font-bold ${bulkPreview.annualDelta > 0 ? "text-green-700" : "text-red-700"}`}>
                                    {bulkPreview.annualDelta > 0 ? "+" : ""}{formatMKD(bulkPreview.annualDelta)}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-3 text-sm p-3 rounded-lg bg-secondary/40">
                                <div className="flex-1">
                                  <p className="text-xs text-muted-foreground mb-0.5">{t("payroll.bulkCurrentGross")}</p>
                                  <p className="font-mono font-semibold">{formatMKD(bulkPreview.totalCurrentGross)}</p>
                                </div>
                                <div className={`text-xl font-bold px-2 ${bulkPreview.delta > 0 ? "text-green-600" : "text-red-600"}`}>→</div>
                                <div className="flex-1 text-right">
                                  <p className="text-xs text-muted-foreground mb-0.5">{t("payroll.bulkNewGross")}</p>
                                  <p className="font-mono font-semibold">{formatMKD(bulkPreview.totalNewGross)}</p>
                                </div>
                              </div>

                              <div>
                                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                                  <div className={`h-full rounded-full transition-all duration-500 ${bulkPreview.delta > 0 ? "bg-green-500" : "bg-red-500"}`} style={{ width: `${Math.min(100, Math.abs((bulkPreview.delta / Math.max(bulkPreview.totalCurrentGross, 1)) * 100) * 5)}%` }} />
                                </div>
                                <p className="text-xs text-muted-foreground text-right mt-0.5">
                                  {bulkPreview.delta > 0 ? "+" : ""}{((bulkPreview.delta / Math.max(bulkPreview.totalCurrentGross, 1)) * 100).toFixed(2)}% {t("payroll.bulkOfTotalGross")}
                                </p>
                              </div>

                              {bulkPreview.byDepartment.length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("payroll.bulkByDept")}</p>
                                  <div className="space-y-1.5">
                                    {bulkPreview.byDepartment.map((d) => (
                                      <div key={d.department} className="flex items-center gap-2 text-xs">
                                        <span className="text-muted-foreground truncate flex-1">{d.department} <span className="opacity-60">({d.count})</span></span>
                                        <div className="w-20 h-1.5 rounded-full bg-secondary overflow-hidden shrink-0">
                                          <div className={`h-full rounded-full ${d.delta > 0 ? "bg-green-500" : "bg-red-500"}`} style={{ width: `${Math.min(100, Math.abs((d.delta / Math.max(bulkPreview.delta, 1))) * 100)}%` }} />
                                        </div>
                                        <span className={`font-mono font-semibold w-28 text-right shrink-0 ${d.delta > 0 ? "text-green-600" : "text-red-600"}`}>
                                          {d.delta > 0 ? "+" : ""}{formatMKD(d.delta)}{t("payroll.perMonth")}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {bulkPreview.skippedCount > 0 && (
                                <p className="text-xs text-amber-600 mt-auto">{bulkPreview.skippedCount} {t("payroll.bulkSkippedNoSalary")}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                        <p className="text-2xl font-bold text-green-700">{bulkResult!.updated}</p>
                        <p className="text-xs text-green-600">{t("payroll.bulkUpdated")}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-center">
                        <p className="text-2xl font-bold text-amber-700">{bulkResult!.skipped}</p>
                        <p className="text-xs text-amber-600">{t("payroll.bulkSkipped")}</p>
                      </div>
                    </div>

                    {bulkResult!.details.updated.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("payroll.bulkUpdated")}</p>
                        <ScrollArea className="max-h-40">
                          <div className="space-y-1">
                            {bulkResult!.details.updated.map((e) => (
                              <div key={e.id} className="flex items-center justify-between text-sm p-2 rounded bg-secondary/50">
                                <span className="font-medium">{e.name}</span>
                                <span className="text-xs text-muted-foreground font-mono">{e.oldSalary.toLocaleString()} → <strong className="text-green-600">{e.newSalary.toLocaleString()}</strong> MKD</span>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}

                    {bulkResult!.details.skipped.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t("payroll.bulkSkipped")}</p>
                        <ScrollArea className="max-h-28">
                          <div className="space-y-1">
                            {bulkResult!.details.skipped.map((e) => (
                              <div key={e.id} className="flex items-center justify-between text-sm p-2 rounded bg-secondary/50">
                                <span className="font-medium">{e.name}</span>
                                <span className="text-xs text-muted-foreground">{e.reason}</span>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                      <Button onClick={() => setBulkDialog(false)} className="gap-2">
                        <CheckCircle className="h-4 w-4" />{t("common.done")}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default Payroll;
